// Un grafic de linii minimal (SVG), fara biblioteca: cateva serii, nu dashboard-uri cu mii de
// puncte (in Elsa: preturile pe luna).
//
// Regulile kitului ELSA pentru grafice: seriile in Grafit si Gri Otel; rosul (#ec3013) marcheaza
// un singur lucru — firma ta, oportunitatea sau alerta — deci cel mult o serie rosie, si doar
// cerut explicit (`color`). Fara gradienti, fara colturi rotunjite.

export interface Series {
  name: string;
  points: [number, number][];
  /** Culoarea seriei; implicit, urmatoarea din paleta (fara rosu). */
  color?: string;
  /** Linie punctata (ex. marginile unui interval). */
  dashed?: boolean;
}

const COLORS = ['#444141', '#9b9797', '#605d5d', '#bab6b6', '#2d2b2b', '#7d7979'];
const colorOf = (s: Series, i: number) => s.color ?? COLORS[i % COLORS.length];

export function LineChart({
  series,
  unit = '',
  height = 140,
  formatX,
}: {
  series: Series[];
  unit?: string;
  height?: number;
  /** Eticheta axei X (implicit: zi/luna ora:minut); `t` in secunde. */
  formatX?: (t: number) => string;
}) {
  const all = series.flatMap((s) => s.points);
  if (!all.length) return <p className="text-sm text-gri-700">Fără date în interval.</p>;
  const xs = all.map((p) => p[0]);
  const ys = all.map((p) => p[1]);
  const x0 = Math.min(...xs);
  const x1 = Math.max(...xs);
  const y1 = Math.max(...ys, 0) * 1.05 || 1;
  const W = 600;
  const H = height;
  const px = (x: number) => ((x - x0) / (x1 - x0 || 1)) * W;
  const py = (y: number) => H - (y / y1) * H;
  const fmt =
    formatX ??
    ((t: number) =>
      new Intl.DateTimeFormat('ro-RO', {
        timeZone: 'Europe/Bucharest',
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      }).format(new Date(t * 1000)));
  return (
    <div>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="h-auto w-full"
        preserveAspectRatio="none"
        role="img"
        aria-label="grafic"
      >
        <line
          x1="0"
          y1={H}
          x2={W}
          y2={H}
          className="stroke-cerneala"
          strokeWidth="2"
          vectorEffect="non-scaling-stroke"
        />
        {series.map((s, i) => (
          <polyline
            key={s.name}
            fill="none"
            stroke={colorOf(s, i)}
            strokeWidth="2"
            strokeDasharray={s.dashed ? '5 4' : undefined}
            vectorEffect="non-scaling-stroke"
            points={s.points.map(([x, y]) => `${px(x).toFixed(1)},${py(y).toFixed(1)}`).join(' ')}
          />
        ))}
      </svg>
      <div className="mt-1 flex flex-wrap justify-between gap-2 text-xs text-gri-700 tabular-nums">
        <span>{fmt(x0)}</span>
        <span>
          max {y1 > 100 ? Math.round(y1 / 1.05) : (y1 / 1.05).toFixed(1)}
          {unit}
        </span>
        <span>{fmt(x1)}</span>
      </div>
      <div className="mt-2 flex flex-wrap gap-4 text-xs font-semibold">
        {series.map((s, i) => (
          <span key={s.name} className="flex items-center gap-1.5">
            <span
              className="inline-block w-4"
              style={{ borderTop: `2px ${s.dashed ? 'dashed' : 'solid'} ${colorOf(s, i)}` }}
            />
            {s.name}
          </span>
        ))}
      </div>
    </div>
  );
}
